import type { TFunction } from 'i18next';

const PERFORMANCE_METRIC_KEYS = new Set([
  'accounts_engaged',
  'click_to_play_rate',
  'clicks',
  'comments',
  'complete_views_30s',
  'engagements',
  'engagement_rate',
  'followers',
  'followers_gained',
  'impressions',
  'likes',
  'page_views',
  'profile_views',
  'reach',
  'reactions',
  'reactions_like',
  'reactions_love',
  'reactions_haha',
  'reactions_wow',
  'reactions_sad',
  'reactions_angry',
  'shares',
  'unfollows',
  'video_views',
  'views',
]);

const CONTENT_METRIC_KEYS = new Set([
  'engagement_rate',
  'impressions',
  'reach',
  'reactions',
  'likes',
  'comments',
  'shares',
  'video_views',
  'engagements',
  'saves',
  'clicks',
  'views',
]);

/** Metrics whose label lives under a key that is not their camel-cased name. */
const PERFORMANCE_METRIC_LABEL_KEYS: Record<string, string> = {
  followers_gained: 'newFollowers',
};

function camelCaseMetricKey(metricKey: string): string {
  return (
    PERFORMANCE_METRIC_LABEL_KEYS[metricKey] ??
    metricKey.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
  );
}

function humanizeMetricKey(metricKey: string): string {
  return metricKey
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatReportMetricLabel(
  metricKey: string,
  sectionKind: 'performance' | 'content',
  t: TFunction,
): string {
  const camelKey = camelCaseMetricKey(metricKey);

  if (sectionKind === 'performance') {
    const performanceKey = `analyticsPerformance.metrics.${camelKey}`;
    if (PERFORMANCE_METRIC_KEYS.has(metricKey)) {
      const label = t(performanceKey, { defaultValue: '' });
      if (label) {
        return label;
      }
    }
    const titledPerformanceKey = `analyticsPerformance.${camelKey}`;
    const titledLabel = t(titledPerformanceKey, { defaultValue: '' });
    if (titledLabel) {
      return titledLabel;
    }
  } else {
    const contentKey = `analyticsContent.metrics.${camelKey}`;
    if (CONTENT_METRIC_KEYS.has(metricKey)) {
      const label = t(contentKey, { defaultValue: '' });
      if (label) {
        return label;
      }
    }
  }

  return humanizeMetricKey(metricKey);
}
